import {controller, target, attr} from '@github/catalyst'
import {verifiedFetch} from '@github-ui/verified-fetch'
import {parseHTML} from '@github-ui/parse-html'
import {getDocumentHtmlSafeNonces, verifyResponseHtmlSafeNonce} from '@github-ui/html-safe-nonce'
import {
  clearValidationErrors,
  validateTokenName,
  validateCustomExpiration,
  validateRepositoryAccess,
} from './personal-access-token-edit'

// Wraps the "Generate token" trigger and its Primer::Alpha::Dialog for the FGPAT confirmation
// flow. We use a custom trigger button rather than Primer's show_button slot so that Enter-key
// form submits are intercepted by #interceptSubmit and routed through the same dialog flow.
@controller('fgpat-confirmation-dialog')
export class FgpatConfirmationDialogElement extends HTMLElement {
  @attr declare confirmationSrc: string
  @target declare body: HTMLElement
  @target declare button: HTMLButtonElement
  @target declare fallback: HTMLTemplateElement
  // Markup is duplicated with the initial visible spinner in the dialog body's Rails template
  // (kept in sync manually) since this template only exists for `resetTrigger` to clone from.
  @target declare spinner: HTMLTemplateElement

  // Non-null while a confirmation request is in flight; also doubles as the source of truth for
  // "is a request pending", so a stale response can be told apart from the request that superseded it.
  #abortController: AbortController | null = null
  // Tracks the form so the listener can still be removed when this element is detached but the
  // form outlives it (e.g. a resource-owner switch), since this.closest('form') returns null by then.
  #form: HTMLFormElement | null = null

  connectedCallback() {
    this.#form = this.closest('form')
    this.#form?.addEventListener('submit', this.#interceptSubmit)
  }

  disconnectedCallback() {
    this.#form?.removeEventListener('submit', this.#interceptSubmit)
    this.#form = null
  }

  // Called on dismissal (including mid-fetch). Aborts any in-flight request and restores the loading
  // state so a future open always starts fresh instead of showing a previous request's result.
  resetTrigger() {
    this.#abortController?.abort()
    this.#abortController = null
    this.button?.removeAttribute('aria-disabled')
    if (this.spinner?.content) {
      this.body?.replaceChildren(this.spinner.content.cloneNode(true))
    }
  }

  // Let the dialog's own submit button through; redirect all other form submissions into the dialog flow.
  // Prevents the form from being submitted directly when the dialog is not open.
  #interceptSubmit = (event: SubmitEvent) => {
    const submitterDialog = event.submitter?.closest('dialog')
    if (submitterDialog instanceof HTMLDialogElement && submitterDialog.open) return
    event.preventDefault()
    event.stopPropagation() // prevent Rails UJS from applying data-disable-with on document-level listeners
    this.requestConfirmation(event)
  }

  async requestConfirmation(event: Event) {
    event.preventDefault()

    const form = this.closest('form')
    if (!form) return

    // Reuses the same validation as the turbo:submit-start handler in personal-access-token-edit.ts.
    clearValidationErrors()
    const firstError = [validateTokenName(), validateCustomExpiration(), validateRepositoryAccess()].find(
      (e): e is NonNullable<typeof e> => Boolean(e),
    )
    if (firstError) {
      firstError.element.scrollIntoView({block: 'center', behavior: 'smooth'})
      return
    }

    // Guard against double-clicks/reopens while a request is already in flight.
    if (this.#abortController) return

    const dialog = this.querySelector('dialog')
    if (!(dialog instanceof HTMLDialogElement)) return

    // Open immediately; focus is trapped in the dialog so the form cannot be edited during the fetch.
    dialog.showModal()
    this.button?.setAttribute('aria-disabled', 'true')

    const abortController = new AbortController()
    this.#abortController = abortController

    try {
      const response = await this.#postConfirmation(form, abortController.signal)
      // Dismissed mid-fetch: resetTrigger already restored the loading state, so don't stomp on it.
      if (abortController.signal.aborted) return
      if (!response.ok) throw new Error(`HTTP ${response.status}`)

      verifyResponseHtmlSafeNonce(getDocumentHtmlSafeNonces(document), response)
      const fragment = parseHTML(document, await response.text())
      this.body.replaceChildren(fragment)
    } catch {
      if (abortController.signal.aborted) return
      // On failure, show the fallback template so the user must explicitly confirm or cancel.
      if (this.fallback?.content) {
        this.body.replaceChildren(this.fallback.content.cloneNode(true))
      }
    } finally {
      // Only clear state if this is still the active request; a superseded request (aborted by a
      // later resetTrigger, which already nulled this out) must not clobber the newer one's state.
      if (this.#abortController === abortController) {
        this.#abortController = null
        this.button?.removeAttribute('aria-disabled')
      }
    }
  }

  async #postConfirmation(form: HTMLFormElement, signal: AbortSignal): Promise<Response> {
    // The endpoint must opt in via allow_verified_fetch.
    return verifiedFetch(this.confirmationSrc, {
      method: 'POST',
      body: new FormData(form),
      headers: {'X-Requested-With': 'XMLHttpRequest'},
      signal,
    })
  }
}
